import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import {
  IOperatorAssignedTaskListVM,
  IOperatorAssignedTaskVM,
  IOperatorBOMData,
  IOperatorLedCutData,
  IOperatorLedLengthData,
} from '../../models/response/IOperatorAssignedTaskResponse';

import {
  ITagRemovePayload,
  ITagTrayPayload,
} from '../../models/forms/IAssignedTag';
import { ICompleteProcess } from '../../models/forms/ICompleteProcess';
import { IRemoveTask } from '../../models/forms/IRemoveTask';
import { IRemoveTray } from '../../models/forms/IRemoveTray';
import {
  IFilteredTraysListVM,
  IFilteredTrayVM,
} from '../../models/response/IFilteredTraysResponse';
import { ITagResponseVM } from '../../models/response/ITagResponse';
import { formatMessage } from '../../translations/format-message';
import {
  GetAllAssignedProcessState,
  GetAllFilteredTraysState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import { ProcessTypeEnum } from '../../constants/enums';
import { IOption } from '../../models/ICommon';
import { ISaveTeplarOrder } from '../../models/forms/ITeplarOrderState';
import {
  ICorrectionFileList,
  ICorrectionRequest,
  IPreviousRaisedRequests,
  IPreviousRequest,
} from '../../models/forms/ICorrectionControl';
const appConfig = config();

export class OperatorStore {
  initialStateValue = ObservableInitialState;
  assignedProcessList: IOperatorAssignedTaskListVM = GetAllAssignedProcessState;
  process: IOperatorAssignedTaskVM | undefined = undefined;
  getAssignedProcessListState = { ...this.initialStateValue };
  completeProcessState = { ...this.initialStateValue };
  getOperatorState = { ...this.initialStateValue };
  getOperatorRequestId = 0;
  OperatorResponse: any = [];
  removeForAbnormalityState = { ...this.initialStateValue };
  getFilteredTraysState = { ...this.initialStateValue };
  filteredTraysList: IFilteredTraysListVM = GetAllFilteredTraysState;
  assignTagState = { ...this.initialStateValue };
  getTrayDetailsOnScanState = { ...this.initialStateValue };
  trayDetail: ITagResponseVM | undefined = undefined;
  removeTagState = { ...this.initialStateValue };
  updateProcessState = { ...this.initialStateValue };
  responseData: string = '';
  saveTeplarOrderState = { ...this.initialStateValue };
  checkProcessStatusState = { ...this.initialStateValue };
  inProgressOrCompletedTrays: string = '';
  completedTrays: string[] = [];
  isPaused: boolean = false;
  isTimerRunning: boolean = false;

  constructor() {
    makeObservable(this, {
      assignedProcessList: observable,
      GetAssignedProcessListService: action,
      process: observable,
      allAssignedTasks: computed,
      getAssignedProcessListState: observable,
      CompleteProcessService: action,
      completeProcessState: observable,
      resetCompleteProcessState: action,
      GetOperatorService: action,
      getOperatorState: observable,
      OperatorResponse: observable,
      resetGetOperatorState: action,
      resetOperatorResponse: action,
      RemoveForAbnormality: action,
      removeForAbnormalityState: observable,
      resetRemoveForAbnormalityState: action,
      operatorTeplarDetails: computed,
      getFilteredTraysState: observable,
      GetFilteredTrays: action,
      getAllFilteredTrays: computed,
      filteredTraysList: observable,
      resetFilteredTraysList: action,
      AssignTagService: action,
      assignTagState: observable,
      resetAssignTagState: action,
      GetTrayDetailsOnScan: action,
      getTrayDetailsOnScanState: observable,
      trayDetail: observable,
      trayDetailComputed: computed,
      RemoveTagService: action,
      removeTagState: observable,
      resetRemoveTagState: action,
      resetGetTrayDetailsOnScanState: action,
      updateProcessState: observable,
      UpdateProcessService: action,
      resetUpdateProcessState: action,
      responseData: observable,
      resetGetAssignedProcessListState: action,
      allAssignedTasksWithoutAssignRemove: computed,
      bomDataList: computed,
      allAssignedTasksNextProcessOptions: computed,
      saveTeplarOrderState: observable,
      SaveTeplarOrderService: action,
      resetSaveTeplarOrderState: action,
      ledCutList: computed,
      ledLengthList: computed,
      // fileUploadState: observable,
      // FileUploadService: action,
      // resetFileUploadState: action,
      // uploadedFile: observable,
      // addCorrectionRequestState: observable,
      // AddCorrectionRequestService: action,
      // resetAddCorrectionRequestState: action,
      // getPreviousCorrectionRequestState: observable,
      // GetPreviousCorrectionRequestService: action,
      // previousRequests: observable,
      // resetGetPreviousCorrectionRequestState: action,
      // deleteFileState: observable,
      // DeleteFileService: action
      CheckProcessStatus: action,
      checkProcessStatusState: observable,
      resetCheckProcessStatusState: action,
      inProgressOrCompletedTrays: observable,
      completedTrays: observable,
      isPaused: observable,
      isTimerRunning: observable,
      setIsTimerRunning: action,
      resetStore: action,
    });
  }

  //#region Actions
  GetAssignedProcessListService = (
    userId: number,
    Model: string,
    HideSettingKey?: string
  ) => {
    this.getAssignedProcessListState.inProgress = true;
    const url = `${URLConstants.GetAllProcessByUserId}/${userId}?Model=${Model}&HideSettingKey=${HideSettingKey}`;
    return baseService
      .getRequest(url)
      .then(
        (
          response: IApiResponse<
            IApiSuccessResponse<IOperatorAssignedTaskListVM>
          >
        ) => {
          if (response.data.Error) {
            this.getAssignedProcessListState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.assignedProcessList = response.data.Data;
            this.getAssignedProcessListState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getAssignedProcessListState.error = err;
      })
      .finally(
        action(() => {
          this.getAssignedProcessListState.inProgress = false;
        })
      );
  };

  /**
  * @returns Promise resolving to whether THIS call succeeded - Complete callers await it so the tray
  * and scanned-tool state are only torn down once the server has actually accepted the Complete.
  */
  CompleteProcessService = (data: ICompleteProcess): Promise<boolean> => {
    this.completeProcessState.inProgress = true;
    const url = URLConstants.CompleteProcess;
    let succeeded = false;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.completeProcessState.error = response.data.Message;
          this.inProgressOrCompletedTrays =
            response.data.Data?.TrayUser?.join('<br/>') ?? '';
          this.completedTrays = response.data.Data?.Trays;
          this.isPaused = response.data.Data?.IsPaused;
        } else {
          toast.success(formatMessage(response.data.Message));
          this.completeProcessState.success = true;
          succeeded = true;
        }
      })
      .catch((err: string) => {
        this.completeProcessState.error = err;
      })
      .finally(
        action(() => {
          this.completeProcessState.inProgress = false;
        })
      )
      .then(() => succeeded);
  };

  GetOperatorService = (
    TenantId: number,
    TrayId: string,
    ProcessId: number,
    ProcessType: number
  ) => {
    this.getOperatorState.inProgress = true;
    const requestId = ++this.getOperatorRequestId;
    const url = `${URLConstants.GetOperator}?TenantId=${TenantId}&TrayId=${TrayId}&ProcessId=${ProcessId}&ProcessType=${ProcessType}`;
    if (TrayId) {
      return baseService
        .getRequest(url)
        .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
          if (response.data.Error) {
            this.getOperatorState.error = `${formatMessage(response?.data?.Message)} ${response?.data?.Data?.userId ? response?.data?.Data?.userId : ''}`;
            toast.error(
              `${formatMessage(response?.data?.Message)} ${response?.data?.Data?.userId ? response?.data?.Data?.userId : ''}`
            );
          } else {
            this.OperatorResponse = response.data.Data;
            this.getOperatorState.success = true;
          }
        })
        .catch((err: string) => {
          this.getOperatorState.error = err;
        })
        .finally(
          action(() => {
            // A newer fetch (e.g. fallback tray after a failed scan) owns the loader now.
            if (requestId === this.getOperatorRequestId) {
              this.getOperatorState.inProgress = false;
            }
          })
        );
    }
  };

  RemoveForAbnormality = (data: IRemoveTask) => {
    this.removeForAbnormalityState.inProgress = true;
    let url = URLConstants.RemoveForAbnormality;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.removeForAbnormalityState.error = response.data.Message;
        } else {
          this.removeForAbnormalityState.success = true;
        }
      })
      .catch((err: string) => {
        this.removeForAbnormalityState.error = err;
      })
      .finally(
        action(() => {
          this.removeForAbnormalityState.inProgress = false;
        })
      );
  };

  GetFilteredTrays = (
    tenantId: number,
    currentPage: number,
    pagerSize: number,
    filterQuery: string
  ) => {
    this.getFilteredTraysState.inProgress = true;
    const url = `${URLConstants.GetFilteredTrays}/${tenantId}?PageNo=${currentPage}&PageSize=${pagerSize}&Filter=${filterQuery}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IFilteredTraysListVM>>) => {
          if (response.data.Error) {
            this.getFilteredTraysState.error = response.data.Message;
            // toast.error(formatMessage(response.data.Message));
          } else {
            this.filteredTraysList = response.data.Data;
            this.getFilteredTraysState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getFilteredTraysState.error = err;
      })
      .finally(
        action(() => {
          this.getFilteredTraysState.inProgress = false;
        })
      );
  };

  AssignTagService = (data: ITagTrayPayload) => {
    this.assignTagState.inProgress = true;
    const url = URLConstants.AssignTags;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<string>>) => {
        if (response.data.Error) {
          this.assignTagState.error = response.data.Message;
          const responseData = response.data.Data;
          this.responseData = responseData;
        } else {
          this.assignTagState.success = true;
        }
      })
      .catch((err: string) => {
        this.assignTagState.error = err;
      })
      .finally(
        action(() => {
          this.assignTagState.inProgress = false;
        })
      );
  };

  GetTrayDetailsOnScan = (tenantId: number, trayId: string) => {
    this.getTrayDetailsOnScanState.inProgress = true;
    const url = `${URLConstants.TagRemoveTrayDetail}/${tenantId}?TagId=${trayId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<ITagResponseVM>>) => {
        if (response.data.Error) {
          this.getTrayDetailsOnScanState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.trayDetail = response.data.Data;
          this.getTrayDetailsOnScanState.success = true;
        }
      })
      .catch((err: string) => {
        this.getTrayDetailsOnScanState.error = err;
      })
      .finally(
        action(() => {
          this.getTrayDetailsOnScanState.inProgress = false;
        })
      );
  };

  RemoveTagService = (data: ITagRemovePayload) => {
    this.removeTagState.inProgress = true;
    const url = URLConstants.RemoveTags;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<string>>) => {
        if (response.data.Error) {
          this.removeTagState.error = response.data.Message;
          const responseData = response.data.Data;
          this.responseData = responseData;
        } else {
          this.removeTagState.success = true;
        }
      })
      .catch((err: string) => {
        this.removeTagState.error = err;
      })
      .finally(
        action(() => {
          this.removeTagState.inProgress = false;
        })
      );
  };

  /**
  * @returns Promise resolving to whether THIS call succeeded - the Start handlers await it so the
  * timer only starts (and tools are only marked as sent) once the server has accepted the Start.
  */
  UpdateProcessService = (data: IRemoveTray): Promise<boolean> => {
    this.updateProcessState.inProgress = true;
    const url = `${URLConstants.UpdateOperatorProcess}`;
    let succeeded = false;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.updateProcessState.error = response.data.Message;
          toast.error(
            `${formatMessage(response.data.Message)}\n${response.data.Data?.join?.('\n') ?? ''}`
          );
        } else {
          this.updateProcessState.success = true;
          succeeded = true;
        }
      })
      .catch((err: string) => {
        this.updateProcessState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.updateProcessState.inProgress = false;
        })
      )
      .then(() => succeeded);
  };

  CheckProcessStatus = (data: IRemoveTray) => {
    this.checkProcessStatusState.inProgress = true;
    const url = `${URLConstants.CheckProcessStatus}`;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.checkProcessStatusState.error = response.data.Message;
          this.inProgressOrCompletedTrays =
            response.data.Data?.TrayUser?.join('<br/>') ?? '';
          this.completedTrays = response.data.Data?.Trays;
          // toast.error(
          //   `${formatMessage(response.data.Message)}\n${response.data.Data?.join('\n') ?? ''}`
          // );
        } else {
          this.checkProcessStatusState.success = true;
        }
      })
      .catch((err: string) => {
        this.checkProcessStatusState.error = err;
      })
      .finally(
        action(() => {
          this.checkProcessStatusState.inProgress = false;
        })
      );
  };

  SaveTeplarOrderService = (data: ISaveTeplarOrder) => {
    this.saveTeplarOrderState.inProgress = true;
    const url = `${URLConstants.SaveTeplarOrder}`;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.saveTeplarOrderState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.saveTeplarOrderState.success = true;
        }
      })
      .catch((err: string) => {
        this.saveTeplarOrderState.error = err;
      })
      .finally(
        action(() => {
          this.saveTeplarOrderState.inProgress = false;
        })
      );
  };

  //#endregion

  //#region Computed
  get allAssignedTasks(): IOperatorAssignedTaskVM[] {
    let processData: IOperatorAssignedTaskVM[] = [];
    // let pickingId: number | null = null;
    if (
      this.assignedProcessList?.UserProcess &&
      this.assignedProcessList.UserProcess?.length > 0
    ) {
      this.assignedProcessList.UserProcess.map((process) => {
        if (process.ProcessName.length > 0 && process.Hide === false) {
          processData.push({
            Id: process.Id,
            ProcessName: process.ProcessName,
            // Type: //formatMessage(ProcessTypeEnum[process.Type]),
            Type: ProcessTypeEnum[process.Type],
            Comment: process.Comment || 'N/A',
            ShortName: process.ShortName,
          });
          // }
        }
      });
    }
    const staticProcesses: IOperatorAssignedTaskVM[] = [
      {
        //assembly
        Id: 9991,
        ProcessName: '組み立て',
        Type: ProcessTypeEnum[2], // adjust enum value as needed
        Comment: 'Assembly',
        ShortName: 'Abl',
      },
      {
        //preprocessing
        Id: 9992,
        ProcessName: '前処理',
        Type: ProcessTypeEnum[2], // adjust enum value as needed
        Comment: 'Preprocessing',
        ShortName: 'Prep',
      },
    ];
    // processData.push(...staticProcesses);
    return processData;
  }

  get allAssignedTasksWithoutAssignRemove(): IOperatorAssignedTaskVM[] {
    let processData: IOperatorAssignedTaskVM[] = [];
    if (
      this.assignedProcessList?.UserProcess &&
      this.assignedProcessList.UserProcess?.length > 0
    ) {
      this.assignedProcessList.UserProcess.map((process) => {
        if (process.ProcessName.length > 0) {
          processData.push({
            Id: process.Id,
            ProcessName: process.ProcessName,
            // Type: formatMessage(ProcessTypeEnum[process.Type]),
            Type: ProcessTypeEnum[process.Type],
            Comment: process.Comment || 'N/A',
            ShortName: process.ShortName,
            Hide: process.Hide,
          });
        }
      });
    }
    return processData;
  }

  get allAssignedTasksNextProcessOptions(): IOption[] {
    let processData: IOption[] = [];
    processData.push({
      id: -3,
      value: 'please_select',
      disabled: false,
    });
    if (
      this.assignedProcessList?.UserProcess &&
      this.assignedProcessList.UserProcess?.length > 0
    ) {
      console.log(ProcessTypeEnum[4]);
      this.assignedProcessList.UserProcess.map((process) => {
        if (process.ProcessName.length > 0 && process.Type !== 4) {
          console.log(process.Type);
          processData.push({
            id: process.Id,
            value: process.ProcessName,
          });
        }
      });
    }
    return processData;
  }

  get getAllFilteredTrays(): IFilteredTrayVM[] {
    let data: IFilteredTrayVM[] = [];
    if (
      this.filteredTraysList &&
      this.filteredTraysList.AssignTrayTagList?.length > 0
    ) {
      this.filteredTraysList.AssignTrayTagList.map(
        ({ EndUserId, Group, Id, LastStatus, TagId, TrayId }) => {
          data.push({
            EndUserId,
            Group,
            Id,
            LastStatus,
            TagId,
            TrayId,
          });
        }
      );
    }
    return data;
  }

  get operatorTeplarDetails(): any {
    if (
      this.OperatorResponse &&
      this.OperatorResponse.Teplars &&
      this.OperatorResponse.Teplars.length > 0
    ) {
      const maxLayout = Math.max(
        ...this.OperatorResponse.Teplars.map((item) => item.Layout)
      );

      return this.OperatorResponse.Teplars.map((item) => {
        let itemsArray = item.Items.toString().split(',');
        while (itemsArray.length < maxLayout) {
          itemsArray.push('-');
        }
        return { ...item, Items: itemsArray.join(',') };
      });
    }
    return [];
  }

  get trayDetailComputed(): ITagResponseVM {
    if (this.trayDetail) {
      const { EndUserId, Group, Id, LastStatus, TagId, TrayId } =
        this.trayDetail;
      return {
        EndUserId,
        Group,
        Id,
        LastStatus,
        TagId,
        TrayId,
      };
    }
    return {
      Group: '',
      Id: -1,
      LastStatus: '',
      TagId: '',
      EndUserId: '',
      TrayId: '',
    };
  }

  get bomDataList(): any {
    let processData: IOperatorBOMData[] = [];
    if (
      this.OperatorResponse &&
      this.OperatorResponse.BOMDataList &&
      this.OperatorResponse.BOMDataList.length > 0
    ) {
      this.OperatorResponse.BOMDataList.map((item) => {
        processData.push({
          Id: item.Id,
          MaterialProductId: item.MaterialProductId,
          katashiki1: item.katashiki1,
          katashiki2: item.katashiki2,
          ManufacturerName: item.ManufacturerName,
          Quantity: item.Quantity,
          SupplyType: formatMessage(item.SupplyType),
          Shortage: item.Shortage,
          Pick: item.Pick,
        });
      });
    }
    return processData;
  }

  get ledCutList(): any {
    let processData: IOperatorLedCutData[] = [];
    if (
      this.OperatorResponse &&
      this.OperatorResponse.LEDCutList &&
      this.OperatorResponse.LEDCutList.length > 0
    ) {
      this.OperatorResponse.LEDCutList.map((item) => {
        processData.push({
          Id: item.Id,
          MaterialProductId: item.MaterialProductId,
          katashiki1: item.katashiki1,
          katashiki2: item.katashiki2,
          ManufacturerName: item.ManufacturerName,
          MaterialCuttingLength: item.MaterialCuttingLength,
        });
      });
    }
    return processData;
  }

  get ledLengthList(): any {
    let processData: IOperatorLedLengthData[] = [];
    if (
      this.OperatorResponse &&
      this.OperatorResponse.LedLengthList &&
      this.OperatorResponse.LedLengthList.length > 0
    ) {
      this.OperatorResponse.LedLengthList.map((item) => {
        processData.push({
          Id: item.Id,
          LEDId: item.LEDId,
          MinValue: item.MinValue,
          MaxValue: item.MaxValue,
          CenterValue: item.CenterValue,
        });
      });
    }
    return processData;
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.assignedProcessList = GetAllAssignedProcessState;
  };

  resetCompleteProcessState = () => {
    this.completeProcessState = { ...this.initialStateValue };
    this.inProgressOrCompletedTrays = '';
    this.completedTrays = [];
    this.isPaused = false;
  };

  resetGetOperatorState = () => {
    // Keep inProgress: the error-recovery path resets state and immediately re-fetches the
    // fallback tray, and wiping the flag would hide that fetch's loader.
    this.getOperatorState = {
      ...this.initialStateValue,
      inProgress: this.getOperatorState.inProgress,
    };
  };

  resetOperatorResponse = () => {
    this.OperatorResponse = [];
  };

  resetRemoveForAbnormalityState = () => {
    this.removeForAbnormalityState = { ...this.initialStateValue };
  };

  resetFilteredTraysList = () => {
    this.getFilteredTraysState = { ...this.initialStateValue };
    this.filteredTraysList = GetAllFilteredTraysState;
  };

  resetAssignTagState = () => {
    this.assignTagState = { ...this.initialStateValue };
  };

  resetRemoveTagState = () => {
    this.removeTagState = { ...this.initialStateValue };
  };

  resetGetTrayDetailsOnScanState = () => {
    this.getTrayDetailsOnScanState = { ...this.initialStateValue };
  };

  resetUpdateProcessState = () => {
    this.updateProcessState = { ...this.initialStateValue };
  };

  resetGetAssignedProcessListState = () => {
    this.getAssignedProcessListState = { ...this.initialStateValue };
  };

  resetSaveTeplarOrderState = () => {
    this.saveTeplarOrderState = { ...this.initialStateValue };
  };

  // resetFileUploadState = () => {
  //   this.fileUploadState = { ...this.initialStateValue };
  // };

  // resetAddCorrectionRequestState = () => {
  //   this.addCorrectionRequestState = { ...this.initialStateValue };
  // };

  // resetGetPreviousCorrectionRequestState = () => {
  //   this.getPreviousCorrectionRequestState = { ...this.initialStateValue };
  // };
  resetCheckProcessStatusState = () => {
    this.checkProcessStatusState = { ...this.initialStateValue };
    // this.inProgressOrCompletedTrays = '';
  };

  setIsTimerRunning = (flag: boolean = false) => {
    this.isTimerRunning = flag;
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.assignedProcessList = GetAllAssignedProcessState;
    this.process = undefined;
    this.getAssignedProcessListState = { ...this.initialStateValue };
    this.completeProcessState = { ...this.initialStateValue };
    this.getOperatorState = { ...this.initialStateValue };
    this.OperatorResponse = [];
    this.removeForAbnormalityState = { ...this.initialStateValue };
    this.getFilteredTraysState = { ...this.initialStateValue };
    this.filteredTraysList = GetAllFilteredTraysState;
    this.assignTagState = { ...this.initialStateValue };
    this.getTrayDetailsOnScanState = { ...this.initialStateValue };
    this.trayDetail = undefined;
    this.removeTagState = { ...this.initialStateValue };
    this.updateProcessState = { ...this.initialStateValue };
    this.responseData = '';
    this.saveTeplarOrderState = { ...this.initialStateValue };
    this.checkProcessStatusState = { ...this.initialStateValue };
    this.inProgressOrCompletedTrays = '';
    this.completedTrays = [];
    this.isPaused = false;
    this.isTimerRunning = false;
  };
  //#endregion
}

export default new OperatorStore();
